/**
 * SSE client built on fetch + ReadableStream.
 *
 * Why not the native EventSource? EventSource doesn't support custom
 * headers (no Authorization), can't POST, and reconnects automatically
 * in ways we don't want for this flow. Fetch + ReadableStream gives us:
 *   - Bearer-token auth
 *   - clean disconnect on abort (signal)
 *   - explicit close semantics
 *
 * Wire format we expect (matches internal/aireview/sse.go output):
 *
 *   event: <name>
 *   data: <single-line JSON>
 *
 * Multi-line `data:` accumulation is supported per the spec, though
 * the backend writes single-line JSON so it's a defensive concession.
 */
import { sessionStore } from './api';

const API_BASE =
  (import.meta as unknown as { env: { VITE_API_BASE?: string } }).env.VITE_API_BASE ?? 'http://localhost:8080';

export interface SSEHandlers {
  /** Called once per event. `name` is the event: value; data is the
   * parsed JSON payload (or the raw string if JSON.parse fails). */
  onEvent(name: string, data: unknown): void;
  /** Called when the stream ends cleanly (server closed). */
  onClose?(): void;
  /** Called on transport failure (network / abort). */
  onError?(err: Error): void;
}

export interface SSEOptions {
  /** Caller-controlled abort. Aborting closes the underlying fetch. */
  signal?: AbortSignal;
}

/**
 * Open an SSE stream against `path` (relative to API_BASE) using a GET
 * request. Auth header is injected from the session store; failure to
 * resolve a token returns a rejected promise.
 *
 * Resolves once headers come back; the body is consumed in the
 * background and callbacks fire as events arrive. Returns a
 * `{ close }` handle so the UI can disconnect proactively.
 */
export async function openSSE(
  path: string,
  handlers: SSEHandlers,
  opts: SSEOptions = {},
): Promise<{ close(): void }> {
  const access = sessionStore.getAccess();
  if (!access) throw new Error('No access token');

  // Compose abort: caller's signal + our own internal one (so close()
  // works even when no caller signal was passed).
  const internal = new AbortController();
  const composite = composeSignals(opts.signal, internal.signal);

  const resp = await fetch(API_BASE + path, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${access}`,
      Accept: 'text/event-stream',
    },
    signal: composite,
  });

  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => '');
    throw new Error(`SSE open failed: ${resp.status} ${text}`);
  }

  pumpStream(resp.body, handlers).finally(() => {
    // pumpStream resolves when the reader hits EOF; tell the caller.
    handlers.onClose?.();
  }).catch((err) => {
    // AbortError is the expected "close()" path; surface anything else.
    if ((err as Error).name === 'AbortError') return;
    handlers.onError?.(err as Error);
  });

  return { close: () => internal.abort() };
}

async function pumpStream(body: ReadableStream<Uint8Array>, handlers: SSEHandlers): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');

  // Per-event accumulator. The SSE wire format separates events with
  // a blank line; within an event, lines starting with `data:` are
  // collected (concatenated with \n per spec).
  let eventName = 'message';
  let dataLines: string[] = [];
  let buffer = '';

  const flush = () => {
    if (dataLines.length === 0 && eventName === 'message') {
      // Pure comment / keepalive; nothing to deliver.
      eventName = 'message';
      return;
    }
    const raw = dataLines.join('\n');
    let parsed: unknown = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Server might send a plain string in data:; fall through.
    }
    handlers.onEvent(eventName, parsed);
    eventName = 'message';
    dataLines = [];
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      // Flush a trailing event without final blank line.
      if (dataLines.length > 0) flush();
      return;
    }
    buffer += decoder.decode(value, { stream: true });

    // Process complete lines; keep the partial trailing line in buffer.
    let nl: number;
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nl).replace(/\r$/, '');
      buffer = buffer.slice(nl + 1);

      if (line === '') {
        flush();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      } else if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      }
      // `id:`, `retry:`, `:` comments — ignored.
    }
  }
}

function composeSignals(a: AbortSignal | undefined, b: AbortSignal): AbortSignal {
  if (!a) return b;
  // Modern AbortSignal.any (Chrome 116+, Safari 17.4+, Firefox 124+).
  if (typeof (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any === 'function') {
    return (AbortSignal as unknown as { any: (s: AbortSignal[]) => AbortSignal }).any([a, b]);
  }
  // Fallback: relay aborts from either source into a new controller.
  const c = new AbortController();
  const onAbort = () => c.abort();
  a.addEventListener('abort', onAbort, { once: true });
  b.addEventListener('abort', onAbort, { once: true });
  return c.signal;
}
